import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Clipboard,
  Download,
  FileCheck2,
  FileSearch,
  FileText,
  Home,
  LoaderCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun } from "docx";
import { generateCoverLetter } from "../services/api";
import { supabase } from "../supabaseClient";

const TONES = [
  { value: "professional", label: "Professional", description: "Polished and suitable for most applications." },
  { value: "confident", label: "Confident", description: "Direct language with clear professional impact." },
  { value: "enthusiastic", label: "Enthusiastic", description: "Positive energy and genuine interest in the role." },
  { value: "friendly", label: "Friendly", description: "Warm, approachable, and still workplace appropriate." },
];

const GENERATION_STAGES = [
  "Reviewing your resume",
  "Understanding the role",
  "Matching your experience",
  "Writing in your selected tone",
  "Finalising your cover letter",
];

export default function CoverLetterPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [optimizedResume, setOptimizedResume] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [tone, setTone] = useState("professional");
  const [coverLetter, setCoverLetter] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [generationStage, setGenerationStage] = useState(0);
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    const stateResume = location.state?.optimizedResume || "";
    const stateJobDescription = location.state?.jobDescription || "";
    const storedResume = localStorage.getItem("optimizedResume") || "";
    const storedJobDescription = localStorage.getItem("jobDescription") || "";

    setOptimizedResume(stateResume || storedResume);
    setJobDescription(stateJobDescription || storedJobDescription);
    setRole(location.state?.role || "");
    setCompany(location.state?.company || "");
    setCoverLetter("");
    setErr("");
    setLimitReached(false);
    setCopyStatus("");
  }, [location.state]);

  useEffect(() => {
    if (!loading || reduceMotion) return undefined;
    const interval = window.setInterval(() => {
      setGenerationStage((current) => Math.min(current + 1, GENERATION_STAGES.length - 1));
    }, 1600);
    return () => window.clearInterval(interval);
  }, [loading, reduceMotion]);

  const onGenerate = async () => {
    if (loading) return;
    setErr("");
    setCoverLetter("");
    setLimitReached(false);
    setCopyStatus("");
    setGenerationStage(0);

    if (!optimizedResume || !jobDescription) {
      setErr("Please provide both the optimized resume and job description.");
      return;
    }

    try {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const email = user?.email;
      if (!email) {
        setErr("You must be logged in to generate a cover letter.");
        return;
      }

      const text = await generateCoverLetter({
        optimizedResume,
        jobDescription,
        tone,
        email,
      });

      if (!text || typeof text !== "string") {
        setErr("Cover letter generation returned no content. Please try again.");
        return;
      }
      setCoverLetter(text);
    } catch (error) {
      const status = error?.status || error?.response?.status;
      if (status === 403) {
        setLimitReached(true);
        setErr("");
      } else {
        console.error(error);
        setErr("Failed to generate cover letter. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const onCopy = async () => {
    if (!coverLetter) return;
    try {
      await navigator.clipboard.writeText(coverLetter);
      setCopyStatus("Copied to clipboard");
    } catch {
      setCopyStatus("Copy failed. Please select and copy the text manually.");
    }
  };

  const onDownloadDocx = async () => {
    if (!coverLetter) return;
    try {
      const paragraphs = coverLetter.split("\n").map(
        (line) => new Paragraph({ children: [new TextRun({ text: line })] })
      );
      const document = new Document({
        sections: [{ properties: {}, children: paragraphs }],
      });
      const blob = await Packer.toBlob(document);
      saveAs(blob, "Cover_Letter.docx");
    } catch {
      setErr("The cover letter could not be downloaded. Please try again.");
    }
  };

  const goInterviewPrep = () => {
    if (!optimizedResume || !jobDescription) {
      setErr("Interview Prep needs your optimised resume and job description.");
      return;
    }
    localStorage.setItem("jobzam.optimizedResume", optimizedResume);
    localStorage.setItem("jobzam.jobDescription", jobDescription);

    navigate("/interview-prep", {
      state: {
        optimizedResume,
        jobDescription,
        role: role || null,
        company: company || null,
        coverLetter: coverLetter || null,
      },
    });
  };

  const hasSource = Boolean(optimizedResume && jobDescription);
  const selectedTone = TONES.find((option) => option.value === tone) || TONES[0];
  const workflowStep = coverLetter ? 3 : loading ? 2 : 1;

  if (!optimizedResume) {
    return (
      <main className="relative min-h-[65vh] overflow-hidden bg-slate-50 px-4 py-10 sm:px-6">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-gradient-to-b from-blue-50 to-transparent" />
        <section className="relative mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_24px_70px_-38px_rgba(37,99,235,0.3)] sm:p-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700"><FileSearch aria-hidden="true" className="h-7 w-7" /></div>
          <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">Create your optimised resume first.</h1>
          <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-slate-600 sm:text-base">Generate a role-specific resume before creating a tailored cover letter.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <button onClick={() => navigate("/upload")} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700"><RefreshCw aria-hidden="true" className="h-4 w-4" /> Go to Resume Optimizer</button>
            <button onClick={() => navigate("/")} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:text-blue-700"><Home aria-hidden="true" className="h-4 w-4" /> Return Home</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 px-3 py-7 min-[390px]:px-4 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-blue-50 to-transparent" />
      <motion.main className="relative mx-auto w-full max-w-7xl" initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <header className="max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-blue-700 shadow-sm"><Sparkles aria-hidden="true" className="h-3.5 w-3.5" /> AI Writing Workspace</div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">Create a tailored <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">cover letter.</span></h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">Use your optimised resume and the job description to generate a role-specific cover letter in your preferred tone.</p>
        </header>

        <ol className="scrollbar-hide mt-6 flex max-w-full touch-pan-x overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-200 bg-white p-2 shadow-sm" aria-label="Cover letter progress">
          {["Resume ready", "Choose tone", "Generate", "Review & download"].map((step, index) => {
            const complete = index < workflowStep;
            const active = index === workflowStep;
            return <li key={step} className="flex min-w-[150px] flex-1 items-center last:min-w-[175px]"><div className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${active ? "bg-blue-50 text-blue-700" : complete ? "text-slate-800" : "text-slate-400"}`} aria-current={active ? "step" : undefined}><span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs ${active ? "bg-blue-600 text-white" : complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{complete ? <Check aria-hidden="true" className="h-4 w-4" /> : index + 1}</span><span className="whitespace-nowrap">{step}</span></div>{index < 3 && <div className={`mx-2 h-px flex-1 ${complete ? "bg-blue-300" : "bg-slate-200"}`} />}</li>;
          })}
        </ol>

        <div className="mt-6 grid min-w-0 gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
          <aside className="min-w-0 space-y-5">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold text-slate-950">Choose your tone</h2>
              <p className="mt-1 text-sm text-slate-600">Select the writing style that best fits your application.</p>
              <div className="mt-4 space-y-2.5" role="radiogroup" aria-label="Cover letter tone">
                {TONES.map((option) => {
                  const selected = tone === option.value;
                  return <button key={option.value} type="button" role="radio" aria-checked={selected} onClick={() => setTone(option.value)} className={`w-full rounded-2xl border p-3.5 text-left outline-none transition focus:ring-4 focus:ring-blue-100 ${selected ? "border-blue-500 bg-blue-50 shadow-sm" : "border-slate-200 hover:border-blue-300"}`}><span className="flex items-center justify-between gap-3"><span className="font-bold text-slate-900">{option.label}</span><span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300"}`}>{selected && <Check aria-hidden="true" className="h-3 w-3" />}</span></span><span className="mt-1 block text-xs leading-5 text-slate-600">{option.description}</span></button>;
                })}
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-bold text-slate-950">Source context</h2>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3"><span className="text-slate-600">Optimised resume</span><span className="inline-flex items-center gap-1 font-bold text-emerald-700"><CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Ready</span></div>
                <div className="flex items-center justify-between gap-3"><span className="text-slate-600">Job description</span><span className={`inline-flex items-center gap-1 font-bold ${jobDescription ? "text-emerald-700" : "text-amber-700"}`}>{jobDescription ? <CheckCircle2 aria-hidden="true" className="h-4 w-4" /> : <AlertTriangle aria-hidden="true" className="h-4 w-4" />}{jobDescription ? "Ready" : "Missing"}</span></div>
                <div className="flex items-center justify-between gap-3"><span className="text-slate-600">Selected tone</span><span className="font-bold text-blue-700">{selectedTone.label}</span></div>
                {(role || company) && <div className="border-t border-slate-100 pt-3"><p className="break-words font-semibold text-slate-800">{role || "Target role"}{role && company ? ` · ${company}` : company}</p></div>}
              </div>
              <button onClick={onGenerate} disabled={loading || !hasSource} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"><Sparkles aria-hidden="true" className="h-4 w-4" />{coverLetter ? "Regenerate Cover Letter" : "Generate My Cover Letter"}<ArrowRight aria-hidden="true" className="h-4 w-4" /></button>
            </section>
          </aside>

          <section className="min-w-0 max-w-full rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_24px_70px_-42px_rgba(15,23,42,0.35)] sm:p-6" aria-labelledby="cover-letter-workspace-title" aria-live="polite">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4"><div><h2 id="cover-letter-workspace-title" className="text-xl font-bold text-slate-950">Cover letter workspace</h2><p className="mt-1 text-xs text-slate-500">Review and edit the generated text before downloading.</p></div>{coverLetter && <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700"><FileCheck2 aria-hidden="true" className="h-4 w-4" /> Ready to use</span>}</div>

            {limitReached && <div className="mt-5 break-words rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"><strong>Free limit reached.</strong> Cover letters are available on Premium. <button onClick={() => navigate("/pricing")} className="ml-1 font-bold text-blue-700 underline">View Premium Plans</button></div>}
            {err && <div role="alert" className="mt-5 break-words rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{err}</div>}

            {loading ? (
              <div className="mx-auto min-w-0 max-w-xl py-5 text-center" aria-busy="true"><LoaderCircle aria-hidden="true" className={`mx-auto h-9 w-9 text-blue-600 ${reduceMotion ? "" : "animate-spin"}`} /><h3 className="mt-4 text-lg font-bold text-slate-950 sm:text-xl">Writing your cover letter</h3><ol className="mt-4 space-y-2 text-left">{GENERATION_STAGES.map((stage, index) => <li key={stage} className={`flex min-w-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-medium sm:px-4 sm:py-3 ${index === generationStage ? "border-blue-200 bg-blue-50 text-blue-800" : index < generationStage ? "border-emerald-100 bg-emerald-50 text-slate-700" : "border-slate-100 text-slate-400"}`}>{index < generationStage ? <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0 text-emerald-600" /> : index === generationStage ? <LoaderCircle aria-hidden="true" className={`h-4 w-4 shrink-0 text-blue-600 ${reduceMotion ? "" : "animate-spin"}`} /> : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-slate-200" />}<span className="min-w-0 break-words">{stage}</span></li>)}</ol></div>
            ) : (
              <div className="mt-5">
                <label htmlFor="generated-cover-letter" className="sr-only">Generated cover letter</label>
                <textarea id="generated-cover-letter" value={coverLetter} onChange={(event) => { setCoverLetter(event.target.value); setCopyStatus(""); }} placeholder="Your tailored cover letter will appear here after generation." className="min-h-[460px] w-full max-w-full resize-y rounded-2xl border border-slate-200 bg-slate-50/50 p-4 text-sm leading-7 text-slate-800 outline-none [overflow-wrap:anywhere] focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100 sm:p-6" />
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <button onClick={onCopy} disabled={!coverLetter} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 sm:w-auto"><Clipboard aria-hidden="true" className="h-4 w-4" /> Copy</button>
                  <button onClick={onDownloadDocx} disabled={!coverLetter} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-40 sm:w-auto"><Download aria-hidden="true" className="h-4 w-4" /> Download DOCX</button>
                  <button onClick={goInterviewPrep} disabled={!hasSource} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-40 sm:ml-auto sm:w-auto"><FileText aria-hidden="true" className="h-4 w-4" /> Continue to Interview Prep <ArrowRight aria-hidden="true" className="h-4 w-4" /></button>
                </div>
                {copyStatus && <p role="status" className={`mt-3 text-sm font-medium ${copyStatus.startsWith("Copied") ? "text-emerald-700" : "text-red-600"}`}>{copyStatus}</p>}
              </div>
            )}
          </section>
        </div>
      </motion.main>
    </div>
  );
}
